/**
 * Copyright © 2026 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import React, { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { DateRangeType } from "../../../components/DateRangePicker";
import { FromTo } from "../../../components/Filters/types";
import { CATEGORY_DEFAULT_OPTION, SortingOptions } from "../../../util/consts";
import { useBranchLocationId } from "../../../util/useBranch";
import { ComponentFilters, SelectedFiltersType } from "../components/ProductFilter/types";
import { PARAM_KEYS, ProductTabIndex } from "../constants";

export function useProductFilters() {
  const [params, setParams] = useSearchParams();
  const branchId = useBranchLocationId();

  // Not URL-backed: the live textbox value (only the committed search goes in the
  // URL), the in-stock toggle, and the store selection.
  const [searchInput, setSearchInput] = useState(params.get(PARAM_KEYS.search) ?? "");
  const [storeId, setStoreId] = useState<string | undefined>(branchId);

  // URL-backed so the filtered state is preserved on refresh and shareable via link.
  const inStock = params.get(PARAM_KEYS.inStock) === "true";

  const category = params.get(PARAM_KEYS.category) ?? CATEGORY_DEFAULT_OPTION;
  const promotions = params.get(PARAM_KEYS.promotion) ?? "";
  const searchText = params.get(PARAM_KEYS.search) ?? "";
  const tab = parseInt(params.get(PARAM_KEYS.tab) ?? "0", 10);
  const currentPage = parseInt(params.get(PARAM_KEYS.page) ?? "1", 10);
  const sortBy =
    params.get(PARAM_KEYS.sortBy) ??
    (tab === ProductTabIndex.Past ? SortingOptions.MostRecent : SortingOptions.BestMatch);

  // dateRange/priceRange are objects, and TypesenseSearch keys its search effect on
  // them by reference — so they must keep a stable identity between renders. Memoise
  // on the raw params so the object only changes when the underlying values do.
  const df = params.get(PARAM_KEYS.dateFrom);
  const dt = params.get(PARAM_KEYS.dateTo);
  const dateRange = useMemo<DateRangeType>(
    () => ({ from: df ? new Date(df) : undefined, to: dt ? new Date(dt) : undefined }),
    [df, dt]
  );
  const pf = params.get(PARAM_KEYS.priceFrom);
  const pt = params.get(PARAM_KEYS.priceTo);
  const priceRange = useMemo<FromTo<number>>(
    () => ({ from: pf ? parseInt(pf) : undefined, to: pt ? parseInt(pt) : undefined }),
    [pf, pt]
  );

  // Writes filter params onto the current URL and snaps back to page 1. Passing null
  // removes a param. Preserves the current pathname, so this works on any route.
  const applyFilters = (updates: Record<string, string | null>) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [key, value] of Object.entries(updates)) {
        if (value) {
          next.set(key, value);
        } else {
          next.delete(key);
        }
      }
      next.set(PARAM_KEYS.page, "1");
      return next;
    });
  };

  // setValue must accept React's SetStateAction (it can be a value or an updater fn) to
  // match the shared ValueAndSetValue type, even though our callers only pass values.
  const selectedFilters: SelectedFiltersType = {
    category: {
      value: category,
      setValue: (next) => {
        const value = typeof next === "function" ? next(category) : next;
        applyFilters({ [PARAM_KEYS.category]: value === CATEGORY_DEFAULT_OPTION ? null : value });
      },
    },
    searchText: {
      value: searchText,
      setValue: (next) => {
        const value = typeof next === "function" ? next(searchText) : next;
        applyFilters({ [PARAM_KEYS.search]: value.trim() || null });
      },
    },
    searchInput: { value: searchInput, setValue: setSearchInput },
    dateRange: {
      value: dateRange,
      setValue: (next) => {
        const value = typeof next === "function" ? next(dateRange) : next;
        applyFilters({
          [PARAM_KEYS.dateFrom]: value.from?.toISOString() ?? null,
          [PARAM_KEYS.dateTo]: value.to?.toISOString() ?? null,
        });
      },
    },
    priceRange: {
      value: priceRange,
      setValue: (next) => {
        const value = typeof next === "function" ? next(priceRange) : next;
        applyFilters({
          [PARAM_KEYS.priceFrom]: value.from?.toString() ?? null,
          [PARAM_KEYS.priceTo]: value.to?.toString() ?? null,
        });
      },
    },
    inStock: {
      value: inStock,
      setValue: (next) => {
        const value = typeof next === "function" ? next(inStock) : next;
        applyFilters({ [PARAM_KEYS.inStock]: value ? "true" : null });
      },
    },
    promotions: {
      value: promotions,
      setValue: (next) => {
        const value = typeof next === "function" ? next(promotions) : next;
        applyFilters({ [PARAM_KEYS.promotion]: value || null });
      },
    },
    storeId: { value: storeId, setValue: setStoreId },
  };

  const setSortBy: React.Dispatch<React.SetStateAction<string>> = (next) => {
    applyFilters({ [PARAM_KEYS.sortBy]: typeof next === "function" ? next(sortBy) : next });
  };

  const setTab: React.Dispatch<React.SetStateAction<number>> = (next) => {
    applyFilters({ [PARAM_KEYS.tab]: (typeof next === "function" ? next(tab) : next).toString() });
  };

  const clearFilters = () => {
    setSearchInput("");
    applyFilters({
      [PARAM_KEYS.category]: null,
      [PARAM_KEYS.search]: null,
      [PARAM_KEYS.dateFrom]: null,
      [PARAM_KEYS.dateTo]: null,
      [PARAM_KEYS.priceFrom]: null,
      [PARAM_KEYS.priceTo]: null,
      [PARAM_KEYS.inStock]: null,
      [PARAM_KEYS.promotion]: null,
    });
  };

  const filterApplied: Record<ComponentFilters, boolean> = {
    [ComponentFilters.SEARCH_TYPESENSE]: searchText.trim() !== "",
    [ComponentFilters.SEARCH_PAST]: searchText.trim() !== "",
    [ComponentFilters.CATEGORY]: category !== CATEGORY_DEFAULT_OPTION,
    [ComponentFilters.DATE]: dateRange.from !== undefined && dateRange.to !== undefined,
    [ComponentFilters.PRICE]: priceRange.from !== undefined && priceRange.to !== undefined,
    [ComponentFilters.IN_STOCK]: inStock,
    [ComponentFilters.PROMOTION]: promotions !== "",
  };

  const getIsFilterApplied = (components: ComponentFilters[]) =>
    components.some((component) => filterApplied[component]);

  return {
    selectedFilters,
    sortBy,
    setSortBy,
    tab,
    setTab,
    currentPage,
    clearFilters,
    getIsFilterApplied,
  };
}
